import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Paper,
  Grid,
  Container,
  Skeleton,
  TextField,
  Typography,
  List,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import * as assignmentService from "../../../services/AssignmentService";
import Notification from "../../../../../components/mui/Notification";
import { Assistant, ConfirmDialog } from "../../../../../components";
import debounce from "lodash/debounce";
import DeleteLoadingButton from "../../../../../components/DeleteLoadingButton";
import { isEpmty } from "../../../../../helper/TextHelper";
import { CASE_STUDY_TYPE_STANDARD } from "../../../helper/Constants";

export const Question = ({ caseStudy, handleUpdatingStatus }) => {
  const caseStudyId = caseStudy.id;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState();
  const [questions, setQuestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [assistanceClicked, setAssistanceClicked] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    caseStudyService
      .existingQuestions(caseStudyId)
      .then((res) => {
        setQuestions(res.data);
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not get questions. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const updateQuestion = useCallback(
    (id, value) => {
      handleUpdatingStatus(true);

      const request = {
        id,
        text: value,
      };
      caseStudyService
        .updateQuestion(request)
        .then((res) => {})
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update Question. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedSave = useMemo(
    () =>
      debounce((id, value) => {
        updateQuestion(id, value);
      }, 2000),
    [updateQuestion]
  );

  const createQuestions = () => {
    setAssistanceClicked(true);
    handleUpdatingStatus(true);
    setCreating(true);
    const request = {
      caseStudyId,
      caseStudyType: CASE_STUDY_TYPE_STANDARD,
      lang: caseStudy.inputParams?.lang,
    };
    caseStudyService
      .createQuestions(request)
      .then((res) => {
        setQuestions(res.data);
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not fetch questions. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setCreating(false);
        handleUpdatingStatus(false);
      });
  };

  const handleChanges = (id, index) => (e) => {
    const newValue = e.target.value;
    const newQuestions = [...questions.filter((item) => item.active)];
    const q = newQuestions[index];
    newQuestions[index] = { ...q, text: newValue };
    setQuestions(newQuestions);

    if (isEpmty(newValue)) {
      setErrors((prevValues) => ({
        ...prevValues,
        [id]: "Can not be empty. Please use the Delete button to delete the question.",
      }));
    } else {
      setErrors((prevValues) => ({
        ...prevValues,
        [id]: undefined,
      }));
      debouncedSave(id, newValue);
    }
  };

  const onDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure you want to delete this question?",
      subTitle: "You can't undo this operation",
      onConfirm: () => deleteQuestion(id),
    });
  };

  const deleteQuestion = (id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    assignmentService
      .deleteQuestion(id)
      .then((ignored) => {
        setNotify({
          isOpen: true,
          message: "Successfully deleted the question.",
          type: "success",
        });
        const updatedList = questions.filter((q) => q.id !== id);
        setQuestions(updatedList);
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not delete the question. Please try again later",
          type: "error",
        });
      });
  };

  const bclAssistantUsed =
    assistanceClicked || questions.some((item) => item.createdByLLM);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 3 } }}>
          <Grid
            container
            spacing={0}
            justify="center"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item>
              <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
                Questions (if needed)
              </Typography>
            </Grid>
            <Grid item style={{ marginLeft: "auto" }}>
              <Assistant
                disabled={creating || loading || bclAssistantUsed}
                onClick={createQuestions}
                loading={creating}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {loading || creating
              ? [...Array(5)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    height={60}
                    sx={{ m: 1 }}
                  />
                ))
              : questions
                  .filter((item) => item.active)
                  .map((qs, qsIndex) => (
                    <List
                      key={qsIndex}
                      sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                      }}
                    >
                      <Grid container spacing={0}>
                        <Grid item xs={12}>
                          <TextField
                            name="text"
                            multiline
                            fullWidth
                            value={qs.text}
                            onChange={handleChanges(qs.id, qsIndex)}
                            error={!!errors[qs.id]}
                            helperText={errors[qs.id]}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            mt: 1,
                            display: "flex",
                            justifyContent: "right", // Pushes icon to the right
                            gap: 1,
                          }}
                        >
                          <DeleteLoadingButton
                            handleClick={() => onDelete(qs.id)}
                          />
                        </Grid>
                      </Grid>
                    </List>
                  ))}
          </Grid>
        </Grid>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Container>
  );
};
